import {Injectable} from '@angular/core';
import {catchError, firstValueFrom, map, of} from 'rxjs';
import {Store} from '@ngrx/store';
import {loadTokenFromLocalStorage, logout, setUserInfo} from '@shared/store/auth/auth.actions';
import {
  loadOrgIdFromLocalStorage,
  setOrganisationUser,
  setSelectedOrganisation
} from '@shared/store/organisation/organisation.actions';
import {OrgService} from '@app/portal/selectOrganisation/service/org.service';
import {AuthService} from '@app/public/auth/service/auth.service';
import {setPermissions, setRole} from '@shared/store/permissions/permissions.action';
import {OrganisationList} from '@graphql/generated';
import {TokenStorageService} from './token-storage.service';

@Injectable({
    providedIn: 'root',
})
export class AppInitService {
    constructor(
        private store: Store,
        private auth: AuthService,
        private orgService: OrgService,
        private tokenStorage: TokenStorageService
    ) { }

    async initializeApp(): Promise<void> {
        // Always load tokens from localStorage first
        this.store.dispatch(loadTokenFromLocalStorage());

        // Only proceed with API calls if user has a valid token
        const token = this.tokenStorage.getCurrentToken();
        if (!token) {
            // User is not logged in, no need to make API calls
            return;
        }

        try {
            await this.fetchUserInfoAndOrganization();
        } catch (error) {
            // console.error('Initialization failed:', error);
            this.store.dispatch(logout());
        }
    }

    /**
     * Method to be called after successful login to fetch user info and organization
     */
    async fetchUserInfoAndOrganization(): Promise<void> {
        try {
            await this.fetchUserInfo();
            await this.fetchOrganisation();
        } catch (error) {
            // console.error('Post-login initialization failed:', error);
            throw error;
        }
    }

    async fetchOrganisation(): Promise<void> {
        // Read orgId from sessionStorage when impersonating, otherwise from localStorage
        const storage = this.tokenStorage.isImpersonationMode() ? sessionStorage : localStorage;
        if (!storage.getItem('orgId')) return;
        const orgId = storage.getItem('orgId')
        try {
            this.store.dispatch(loadOrgIdFromLocalStorage());
            const organisations = await firstValueFrom(
                //add based on filters
                this.orgService.listUserOrgs({ pagination: { first: 1 }, filters: { id: orgId } }).pipe(
                    map(data => data.organisations),
                    catchError((error) => {
                        // console.error('Error fetching organisations:', error);
                        this.store.dispatch(setSelectedOrganisation({ selectedOrganisation: null }));
                        return of([]);
                    })
                )
            );
            if (organisations && organisations.length > 0) {
              //
                let org: OrganisationList = organisations[0]
                const selectedOrganisation = org.organisation
                this.store.dispatch(setSelectedOrganisation({ selectedOrganisation }));
                const organisationUser = org.organisationUser
                this.store.dispatch(setOrganisationUser({ organisationUser }));
                const permissions = org.permissions;
                this.store.dispatch(setPermissions({ permissions, permissionScope: 'organisation' }));
                // const role = null;
                // this.store.dispatch(setRole({role}));
            } else {
                // console.warn('No organisations found');
                this.store.dispatch(setSelectedOrganisation({ selectedOrganisation: null }));
            }
        } catch (error) {
            // console.error('Error in fetchOrganisation:', error);
            throw error;
        }
    }

    private async fetchUserInfo(): Promise<any> {
        try {
            const userInfo = await firstValueFrom(
                this.auth.me().pipe(
                    map((res: any) => {

                        const userId = res?.data?.me?.user?.id;
                        if (userId) {
                            localStorage.setItem('userId', userId);
                        }
                        return res?.data?.me;
                    }),
                    catchError((error) => {
                        // console.error('Error in "me" query:', error);
                        this.store.dispatch(setUserInfo(null)); // Clear user info on error
                        return of(null);
                    })
                )
            );
            if (userInfo) {
              //
                this.store.dispatch(setUserInfo({ userInfo: userInfo?.user }));
                // const permissions = null
                // this.store.dispatch(setPermissions({permissions}))
                const role = userInfo?.user?.role
                this.store.dispatch(setRole({ role }))
                if (userInfo?.staffRole) {
                    this.store.dispatch(setPermissions({ permissions: userInfo?.staffRole?.permissions, permissionScope: "user" }))
                }
            } else {
                throw new Error('User info not available');
            }
            return userInfo;
        } catch (error) {
            // console.error('Error fetching user info:', error);
            throw error;
        }
    }
}
